import { createContext, PropsWithChildren, useMemo } from 'react';

import { SelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { useGuardedContext } from '@/hooks/useGuardedContext';

import { InsurancePolicyTypeCopyMap } from '../../InsurancePolicyDetailsSubform/InsurancePolicyDetailsSubform.types';
import {
  HypotheticalIlitModalData_HypotheticalInsuranceFragment,
  HypotheticalIlitModalData_InsurancePolicyFragment,
} from './graphql/HypotheticalILITModalData.generated';

export interface HypotheticalILITModalContextShape {
  householdId: string;
  waterfallId: string;
  isLoading: boolean;
  insurancePolicyOptions: SelectInputOption<string>[];
  existingPolicies: HypotheticalIlitModalData_InsurancePolicyFragment[];
  initialHypotheticalInsurance: HypotheticalIlitModalData_HypotheticalInsuranceFragment | null;
}

export const HypotheticalILITModalContext =
  createContext<HypotheticalILITModalContextShape>({
    householdId: '',
    waterfallId: '',
    isLoading: false,
    insurancePolicyOptions: [],
    existingPolicies: [],
    initialHypotheticalInsurance: null,
  });

export const useHypotheticalILITModalContext = () => {
  return useGuardedContext(
    HypotheticalILITModalContext,
    'HypotheticalILITModalContextProvider'
  );
};

export interface HypotheticalILITModalContextProviderProps
  extends PropsWithChildren<
    Omit<HypotheticalILITModalContextShape, 'insurancePolicyOptions'>
  > {
  existingPolicies: HypotheticalIlitModalData_InsurancePolicyFragment[];
}

export function HypotheticalILITModalContextProvider({
  householdId,
  waterfallId,
  isLoading,
  existingPolicies,
  initialHypotheticalInsurance,
  children,
}: HypotheticalILITModalContextProviderProps) {
  const value = useMemo<HypotheticalILITModalContextShape>(() => {
    const validExistingPolicies = existingPolicies.filter((policy) => {
      // this chain should match the one in existingInsurancePolicy
      const policyHolderID =
        policy.cltTrust?.entity?.id ||
        policy.crtTrust?.entity?.id ||
        policy.daf?.entity?.id ||
        policy.ilitTrust?.entity?.id ||
        policy.insurancePersonalAccount?.entity?.id ||
        policy.irrevocableTrust?.entity?.id ||
        policy.privateFoundation?.entity?.id ||
        policy.revocableTrust?.entity?.id ||
        policy.slatTrust?.entity?.id;
      return !!policyHolderID;
    });

    const insurancePolicyOptions = validExistingPolicies.map<
      SelectInputOption<string>
    >((policy) => ({
      value: policy.id,
      display: policy.carrierName
        ? `${InsurancePolicyTypeCopyMap[policy.kind]}, ${policy.carrierName}`
        : InsurancePolicyTypeCopyMap[policy.kind],
      secondaryText: policy.policyHolders?.length
        ? `Insured persons: ${policy.policyHolders.map((person) => person.displayName).join(', ')}`
        : undefined,
    }));

    return {
      householdId,
      waterfallId,
      isLoading,
      insurancePolicyOptions,
      existingPolicies: validExistingPolicies,
      initialHypotheticalInsurance,
    };
  }, [
    existingPolicies,
    householdId,
    waterfallId,
    isLoading,
    initialHypotheticalInsurance,
  ]);

  return (
    <HypotheticalILITModalContext.Provider value={value}>
      {children}
    </HypotheticalILITModalContext.Provider>
  );
}
